import { BLOG_API_ORIGIN } from "./blogApi.js";
// Client-side half of the blog view policy. The server (BlogEngagementController
// + BlogViewService, table blog_view_log) enforces the real one: one counted
// view per browser per blog per 24 h, crawlers excluded, rate limited. This
// module only avoids sending requests that cannot count.
//
//   POST {api}/api/blogs/{id}/view   -> { counted, views }   (registers a view)
//   GET  {api}/api/blogs/{id}/views  -> { views }            (read-only)
//
// Pure functions with injectable fetch/storage/clock so they run the same in
// the browser and under `node --test`.


export const VIEW_WINDOW_MS = 24 * 60 * 60 * 1000;
const VISITOR_ID_KEY = "hachionVisitorId";

export const formatViews = (n) => `${Number(n).toLocaleString("en-US")} ${Number(n) === 1 ? "view" : "views"}`;

// Server-provided count when it is a usable number, else null ("unknown", not 0).
export function toViewCount(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function browserStorage() {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function newId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function getVisitorId(storage) {
  try {
    let id = storage.getItem(VISITOR_ID_KEY);
    if (!id || !/^[A-Za-z0-9-]{16,64}$/.test(id)) {
      id = newId();
      storage.setItem(VISITOR_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

export function createViewTracker({ fetchImpl, storage, now = Date.now, apiBase = BLOG_API_ORIGIN } = {}) {
  const inFlight = new Map();
  const doFetch = (...args) => (fetchImpl || fetch)(...args);
  const store = () => storage || browserStorage();

  async function run(id) {
    const base = `${apiBase}/api/blogs/${id}`;
    const s = store();
    const key = `blogView:${id}`;
    let last = 0;
    try {
      last = Number(s?.getItem(key)) || 0;
    } catch {
      // storage blocked: the server-side window still applies
    }

    if (now() - last < VIEW_WINDOW_MS) {
      // Already counted in this period: do not POST, just read the current total.
      try {
        const res = await doFetch(`${base}/views`);
        return res.ok ? toViewCount((await res.json())?.views) : null;
      } catch {
        return null;
      }
    }

    // Stored *before* the request so two concurrent callers cannot both fire;
    // removed again on failure so a failed attempt can be retried.
    try {
      s?.setItem(key, String(now()));
    } catch {
      // ignore
    }
    try {
      const visitorId = s ? getVisitorId(s) : null;
      const res = await doFetch(`${base}/view`, {
        method: "POST",
        keepalive: true,
        headers: visitorId ? { "X-Visitor-Id": visitorId } : {},
      });
      if (!res.ok) throw new Error(String(res.status));
      return toViewCount((await res.json())?.views);
    } catch {
      try {
        s?.removeItem(key);
      } catch {
        // ignore
      }
      return null;
    }
  }

  // Resolves to the authoritative count, or null when unavailable. Concurrent
  // calls for one blog (Strict Mode double effect, remounts during client-side
  // navigation) share one request and all receive its result.
  function track(blogId) {
    const id = Number(blogId);
    if (!Number.isInteger(id) || id <= 0) return Promise.resolve(null);
    if (inFlight.has(id)) return inFlight.get(id);
    const p = run(id).finally(() => inFlight.delete(id));
    inFlight.set(id, p);
    return p;
  }

  return { track };
}

export const blogViewTracker = createViewTracker();
